/**
* @description: 设备工时统计（按月）
*/
<template>
    <div class="finance_box" ref="myElementRef">
        <div ref="searchBox">
            <hc-search :data="searchData" @serch="onSearch">
                <template #btns>
                    <el-button type="success" icon="Download" @click="exportData">{{ t('deviceWorkTime.exportExcel') }}</el-button>
                </template>
            </hc-search>
        </div>
        <div class="tip-bar">
            {{ t('deviceWorkTime.tip') }}
        </div>
        <div class="table_box">
            <hc-table :loading="state.loading"
                :tableData="state.tableData"
                :column="state.tableHeader"
                :offsetHeight="state.offsetHeight"
                :maxPage="maxPage"
                @pageChange="pageChange"
                @limitChange="limitChange"
            >
                <template #title="{ row }">
                    <el-button class="opBtn" size="small" type="primary" link @click="toDetail(row, 'workTime')">
                        {{ row.title }}
                    </el-button>
                </template>
                <template #operation="scope">
                    <el-button
                        class="opBtn"
                        size="small"
                        type="primary"
                        link
                        @click="toDetail(scope.row, 'workTime')"
                    >{{ t('common.detail') }}</el-button>
                    <el-button
                        class="opBtn"
                        size="small"
                        type="primary"
                        link
                        @click="openOperators(scope.row)"
                    >{{ t('deviceWorkTime.operatorDetail') }}</el-button>
                </template>
            </hc-table>
        </div>
        <DeviceWorkTimeOperators ref="operatorsRef" />
    </div>
</template>

<script setup>
import hcSearch from "@/components/hc-search/index.vue";
import hcTable from "@/components/hc-table/index.vue";
import DeviceWorkTimeOperators from "./components/deviceWorkTimeOperators.vue";
import { onMounted, reactive, ref, nextTick, onUnmounted, toRaw } from "vue";
import { useRouter } from "vue-router";
import { ElMessage } from "element-plus";
import { workTimeStatsApi, workSiteOptionsApi } from "@/api/system/device";
import { exportExcel } from "@/utils/apiBase";
import { listToMap } from "@/utils/utils";
import { usePageI18n } from "@/composables/usePageI18n.js";
import { applySearchLabels, applyColumnLabels, applyOptionLabels } from "@/utils/listPageI18n.js";

const myElementRef = ref();
const searchBox = ref();
const operatorsRef = ref();
const router = useRouter();

let page = ref(1);
let limit = ref(20);
let maxPage = ref(0);

const statusOptions = [
    { label: "", value: 99 },
    { label: "", value: 1 },
    { label: "", value: 0 },
];

const onlyActiveOptions = [
    { label: "", value: 0 },
    { label: "", value: 1 },
];

const applyLabels = () => {
    applySearchLabels(searchData, {
        time: { labelKey: 'deviceWorkTime.month' },
        site: { labelKey: 'deviceWorkTime.site', placeholderKey: 'deviceWorkTime.allSites' },
        keywords: { labelKey: 'field.deviceName' },
        model: { labelKey: 'field.deviceCode' },
        status: { labelKey: 'field.status' },
        only_active: { labelKey: 'deviceWorkTime.scope' },
    });
    applyColumnLabels(state.column, {
        code: 'field.deviceCode',
        title: 'field.deviceName',
        primary_site: 'deviceWorkTime.primarySite',
        model: 'field.model',
        work_days: 'deviceWorkTime.workDays',
        operator_count: 'deviceWorkTime.operatorCount',
        standard_time: 'deviceWorkTime.standardTime',
        ot_time: 'deviceWorkTime.otTime',
        zhi_time: 'deviceWorkTime.zhiTime',
        tongxiao_time: 'deviceWorkTime.tongxiaoTime',
        total_time: 'deviceWorkTime.totalTime',
        operation: 'table.operation',
    });
    applyOptionLabels(statusOptions, {
        99: 'common.all',
        1: 'status.available',
        0: 'status.warehouse',
    });
    applyOptionLabels(onlyActiveOptions, {
        0: 'deviceWorkTime.allDevices',
        1: 'deviceWorkTime.withHoursOnly',
    });
    const siteField = searchData.find((i) => i.field === 'site');
    if (siteField?.children?.[0]) {
        siteField.children[0].label = t('deviceWorkTime.allSites');
    }
};
const { t } = usePageI18n(applyLabels);

const searchData = reactive([
    {
        label: "月份",
        type: "month",
        value: "",
        field: "time",
    },
    {
        label: "工地",
        type: "select",
        value: "",
        field: "site",
        placeholder: "全部工地",
        children: [{ label: "全部工地", value: "" }],
        clearable: true,
    },
    {
        label: "设备名称",
        type: "input",
        value: "",
        placeholder: "",
        field: "keywords",
    },
    {
        label: "機號",
        type: "input",
        value: "",
        placeholder: "",
        field: "model",
    },
    {
        label: "状态",
        type: "select",
        children: statusOptions,
        value: 99,
        defaultValue: 99,
        field: "status",
        clearable: false,
    },
    {
        label: "范围",
        type: "select",
        children: onlyActiveOptions,
        value: 0,
        defaultValue: 0,
        field: "only_active",
        clearable: false,
    },
]);

const state = reactive({
    loading: false,
    column: [
        { prop: "id", show: false, label: "ID" },
        { show: true, type: "index", label: "序号", width: 60 },
        { prop: "code", show: true, label: "機號", width: 140 },
        { prop: "title", show: true, label: "设备名称", width: 160 },
        { prop: "primary_site", show: true, label: "主要工地", width: 140 },
        { prop: "model", show: true, label: "机型", width: 120 },
        { prop: "work_days", show: true, label: "出勤天数", width: 90 },
        { prop: "operator_count", show: true, label: "机手人数", width: 90 },
        { prop: "standard_time", show: true, label: "落机工时(h)", width: 100 },
        { prop: "ot_time", show: true, label: "OT工时(h)", width: 90 },
        { prop: "zhi_time", show: true, label: "中直工时(h)", width: 100 },
        { prop: "tongxiao_time", show: true, label: "通宵工时(h)", width: 100 },
        { prop: "total_time", show: true, label: "合计(h)", width: 90 },
        { prop: "operation", show: true, label: "操作", width: 160 },
    ],
    offsetHeight: 0,
    tableHeader: [],
    tableData: [],
});

const tableHeaderFn = () => {
    state.tableHeader = state.column.filter((v) => v.show);
};

const offHeighths = () => {
    if (myElementRef.value) {
        state.offsetHeight =
            myElementRef.value?.offsetHeight - 120 - searchBox.value?.offsetHeight;
    }
};

const getMonthValue = () => {
    return toRaw(searchData.find((i) => i.field === "time") ?? {}).value ?? "";
};

const buildParams = () => {
    const mapped = listToMap(searchData, { value: "value", key: "field" });
    const params = {
        page: page.value,
        limit: limit.value,
        time: mapped.time ?? "",
        keywords: mapped.keywords ?? "",
        model: mapped.model ?? "",
        status: mapped.status ?? 99,
        only_active: mapped.only_active ?? 0,
    };
    if (mapped.site) {
        params.site = mapped.site;
    }
    return params;
};

const getSiteValue = () => {
    return toRaw(searchData.find((i) => i.field === "site") ?? {}).value ?? "";
};

const loadSiteOptions = async () => {
    const month = getMonthValue();
    const siteField = searchData.find((i) => i.field === "site");
    if (!month) {
        if (siteField) {
            siteField.children = [{ label: t('deviceWorkTime.allSites'), value: "" }];
        }
        return;
    }
    try {
        const res = await workSiteOptionsApi(JSON.stringify({ time: month }));
        const options = (res?.data ?? []).map((i) => ({
            label: i.site,
            value: i.site,
        }));
        if (siteField) {
            siteField.children = [{ label: t('deviceWorkTime.allSites'), value: "" }, ...options];
            const current = siteField.value;
            if (current && !options.some((o) => o.value === current)) {
                siteField.value = "";
            }
        }
    } catch {
        if (siteField) {
            siteField.children = [{ label: t('deviceWorkTime.allSites'), value: "" }];
        }
    }
};

const getSearch = () => {
    const params = buildParams();
    if (!params.time) {
        ElMessage.warning(t('deviceWorkTime.selectMonth'));
        return;
    }
    state.loading = true;
    workTimeStatsApi(JSON.stringify(params))
        .then((res) => {
            maxPage.value = res?.maxPage ?? 0;
            state.tableData = res?.data ?? [];
        })
        .catch(() => {
            maxPage.value = 0;
            state.tableData = [];
        })
        .finally(() => {
            state.loading = false;
        });
};

const onSearch = async () => {
    page.value = 1;
    await loadSiteOptions();
    getSearch();
};

const pageChange = (currentPage) => {
    page.value = currentPage;
    getSearch();
};

const limitChange = (currentLimit) => {
    limit.value = currentLimit;
    getSearch();
};

const openOperators = (row) => {
    operatorsRef.value?.open(row, getMonthValue(), getSiteValue());
};

const toDetail = (row, tab = "workTime") => {
    router.push({
        path: "/equipment/device-detail",
        query: { id: row.id, tab },
    });
};

const exportData = async () => {
    const params = buildParams();
    if (!params.time) {
        ElMessage.warning(t('deviceWorkTime.selectMonth'));
        return;
    }
    const exportParams = { time: params.time };
    if (params.keywords) exportParams.keywords = params.keywords;
    if (params.model) exportParams.model = params.model;
    if (params.status != null && params.status < 99) exportParams.status = params.status;
    if (params.only_active === 1) exportParams.only_active = 1;
    if (params.site) exportParams.site = params.site;
    const siteSuffix = params.site ? `-${params.site}` : "";
    const { ok, msg } = await exportExcel(
        "Excel/deviceWorkTimeExcel",
        exportParams,
        `设备工时统计-${params.time}${siteSuffix}.xls`
    );
    if (!ok) {
        ElMessage.error(msg || t('deviceWorkTime.exportFailed'));
    }
};

onMounted(async () => {
    applyLabels();
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    searchData[0].value = `${now.getFullYear()}-${month}`;
    nextTick(() => offHeighths());
    window.onresize = () => nextTick(() => offHeighths());
    tableHeaderFn();
    await loadSiteOptions();
    getSearch();
});

onUnmounted(() => {
    window.onresize = null;
});
</script>

<style lang="less" scoped>
.finance_box {
    flex: 1;
    overflow: auto;
    background-color: #fff;
    border-radius: 6px;
    padding: 10px;
    margin-right: 10px;
}

.tip-bar {
    margin: 0 0 10px;
    padding: 8px 12px;
    font-size: 13px;
    color: #606266;
    background: #f4f8ff;
    border-radius: 4px;
    line-height: 1.5;
}

.table_box {
    width: 100%;
}

.opBtn {
    margin: 0;
}
</style>
